import React, { useState, useEffect, useCallback } from "react";
import {
  auth,
  signInWithPhoneNumber,
  googleProvider,
  appleProvider,
  firebaseErrorHandler,
  signUpWithEmail,
  PhoneAuthProvider,
  signInWithPopup,
  signInWithEmail,
  resetPassword,
} from "../service/firebase";
import {
  Container,
  Button,
  Box,
  Typography,
  OutlinedInput,
  InputAdornment,
  Grid,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { login, backend } from "../reducers/userReducer";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ErrorComponent from "../components/errorComponent";
import Loader from "../components/loader";
import {
  analytics,
  getUserInfo,
  handlePhoneNumberError,
  initializeRecaptcha,
  triggerHaptic,
} from "../helper/helper";
import CroboContainer from "../components/croboContainer";
import OTPInput from "react-otp-input";
import GoogleButton from "../components/googleButton";
import AppleButton from "../components/appleButton";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { isIos, isWeb } from "../utils/platform";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/LocalPhoneOutlined";
import EmailIcon from "@mui/icons-material/MarkunreadOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { useTheme } from "@mui/material/styles";
import { sendEmailVerification } from "firebase/auth";
import CloseIcon from "@mui/icons-material/Close";

const Login = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [showSignupAlert, setShowSignupAlert] = useState(false);
  const [loginMethod, setLoginMethod] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loginPopupClosed, setLoginPopupClosed] = useState(false);
  const [emailVerificationCooldown, setEmailVerificationCooldown] =
    useState(false);
  const [emailVerificationCooldownTime, setEmailVerificationCooldownTime] =
    useState(60);
  const [verificationEmailSignup, setVerificationEmailSignup] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, pin, isNewUser,loading } = useSelector((state) => state.userAuth);
  const theme = useTheme();

  const handleVerifyOtp = useCallback(async () => {
    setLoader(true);
    analytics.track("OTP Verification Attempt", { phone, otp });

    try {
      let result;
      let authToken;
      if (isWeb()) {
        // Web implementation
        result = await confirmationResult.confirm(otp);
      } else {
        // Native implementation
        result = await FirebaseAuthentication.confirmVerificationCode({
          verificationId: confirmationResult,
          verificationCode: otp,
        });

        authToken = await FirebaseAuthentication.getIdToken(true);
      }
      analytics.track("Login Success", { phone, isNewUser });
      dispatch(
        login({
          phone: result.user.phoneNumber,
          authToken: isWeb() ? result.user.accessToken : authToken.token,
        })
      );
    } catch (error) {
      handlePhoneNumberError(error, setError, setLoader, setCooldown, phone);
    }
  }, [confirmationResult, dispatch, otp, phone]);

  // Add effect to auto-verify OTP when length is 6
  useEffect(() => {
    if (otp.length === 6 && otpSent) {
      handleVerifyOtp();
    }
  }, [otp, handleVerifyOtp, otpSent]);

  useEffect(() => {
    const handleProfileNavigation = async () => {
      console.log(isNewUser, pin, profile);
      await triggerHaptic("heavy");
      // Only navigate if not already on the target page
      if (isNewUser && location.pathname !== "/new") {
        navigate("/new");
      } else if (!pin && location.pathname !== "/pin/set") {
        navigate("/pin/set");
      }
    };
    resetState();
    if (profile) {
      handleProfileNavigation();
    }
  }, [navigate, profile, pin, isNewUser, location.pathname]);

  useEffect(() => {
    if (isWeb()) {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initializeRecaptcha);
      } else {
        initializeRecaptcha();
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let interval;

    if (cooldown) {
      interval = setInterval(() => {
        if (!isMounted) return;

        setCooldownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            if (isMounted) {
              setCooldown(false);
              setIsResendDisabled(false);
            }
            return 60;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [cooldown]);

  useEffect(() => {
    let isMounted = true;
    let interval;

    if (emailVerificationCooldown) {
      interval = setInterval(() => {
        if (!isMounted) return;

        setEmailVerificationCooldownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            if (isMounted) {
              setEmailVerificationCooldown(false);
            }
            return 60;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [emailVerificationCooldown]);

  const resetState = () => {
    setPhone("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLoginMethod("");
    setShowLoginForm(false);
    setShowForgotPassword(false);
    setOtp("");
    setOtpSent(false);
    setConfirmationResult(null);
    setError("");
  };

  const checkUserExists = async (phoneNumber) => {
    try {
      const response = await axios.post(`${backend}/users/exist`, {
        country_code: "1",
        phone_number: phoneNumber,
        client_id: "APIGetBitRemitWeb",
      });
      return response.data.status_code === 200;
    } catch (error) {
      if (error.response && error.response.data.status_code === 404) {
        return false;
      }
      throw error;
    }
  };

  const handleSignInWithPhone = async () => {
    setError("");
    try {
      setLoader(true);
      analytics.track("OTP Requested", { phone: `+1${phone}` });

      if (cooldown) {
        setError("Please wait a moment before retrying.");
        setLoader(false);
        return;
      }

      // Check if user exists
      const userExists = await checkUserExists(phone);
      if (!userExists && location.pathname === "/login") {
        setShowSignupAlert(true);
        setLoader(false);
        setTimeout(() => {
          navigate("/signup");
        }, 2000);
        return;
      }

      if (isWeb()) {
        // Web implementation
        const appVerifier = window.recaptchaVerifier;
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          `+1${phone}`,
          appVerifier
        );
        setOtpSent(true);
        setLoader(false);
        setIsResendDisabled(true);
        setCooldown(true);
        analytics.track("OTP Sent", { phone });
        setConfirmationResult(confirmationResult);
      } else {
        // Native implementation
        await FirebaseAuthentication.signInWithPhoneNumber({
          phoneNumber: `+1${phone}`,
        });

        // Add listener for phoneCodeSent event
        await FirebaseAuthentication.addListener("phoneCodeSent", (event) => {
          setConfirmationResult(event.verificationId);
          setOtpSent(true);
          setLoader(false);
          setIsResendDisabled(true);
          setCooldown(true);
          analytics.track("OTP Sent", { phone });
        });
      }
    } catch (error) {
      handlePhoneNumberError(error, setError, setLoader, setCooldown, phone);
    }
  };

  const handleResendOtp = () => {
    analytics.track("OTP Resent", { phone });
    setIsResendDisabled(true);
    handleSignInWithPhone();
  };

  const handleOAuthSignInv2 = async (provider, method) => {
    setError("");
    try {
      setLoader(true);
      let result;
      let authToken;
      if (isWeb()) {
        // Web implementation
        result = await signInWithPopup(auth, provider);
      } else {
        // Native implementation
        if (method === "Google") {
          result = await FirebaseAuthentication.signInWithGoogle({
            prompt: "select_account", // Force account selection
          });
          authToken = await FirebaseAuthentication.getIdToken(true);
        } else if (method === "Apple") {
          result = await FirebaseAuthentication.signInWithApple();
          authToken = await FirebaseAuthentication.getIdToken(true);
        }
      }
      getUserInfo(dispatch);
      analytics.track("Login Success", { method, isNewUser });
      dispatch(
        login({
          phone: result.user.phoneNumber,
          authToken: isWeb() ? result.user.accessToken : authToken.token,
        })
      );
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        setLoginPopupClosed(true);
        // Clear the error state after showing the popup closed message
        setTimeout(() => {
          setLoginPopupClosed(false);
          setError("");
        }, 3000);
      } else {
        const errorMessage = firebaseErrorHandler(
          typeof error.code === "string" ? error.code : error.message
        );
        setError(errorMessage);
      }
      setLoader(false);
      analytics.track(`${method} Sign-In Failed`, { error: JSON.stringify(error), errorCode: error.code });
    }
  };

  const handleGoogleSignIn = () =>
    handleOAuthSignInv2(googleProvider, "Google");
  const handleAppleSignIn = () => handleOAuthSignInv2(appleProvider, "Apple");

  const handleChangePhoneNumber = () => {
    analytics.track("Phone Number Change Initiated", { phone });
    setOtpSent(false);
    setConfirmationResult(null);
    setOtp("");
    setError("");
    setIsResendDisabled(false);
    setCooldown(false);
    setCooldownTime(60);
    setPhone("");
    setLoader(false);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handleEmailLogin = async () => {
    setError("");
    try {
      const passwordValidationError = validatePassword(password);
      if (location.pathname === "/signup") {
        if (passwordValidationError) {
          setError(passwordValidationError);
          return;
        }
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }
      }

      setLoader(true);
      analytics.track(
        location.pathname === "/signup"
          ? "Email Signup Attempt"
          : "Email Login Attempt",
        { email }
      );

      let authToken;
      let user;
      if (isWeb()) {
        if (location.pathname === "/signup") {
          user = await signUpWithEmail(email, password);
          // Send verification email
          await sendEmailVerification(user, {
            url: "https://www.crobo.money/login",
            handleCodeInApp: false,
          });
          setVerificationEmailSent(true);
          setEmailVerificationCooldown(true);
          setEmailVerificationCooldownTime(60);
          // Don't dispatch login action for signup - wait for email verification
          analytics.track("Signup Success", { method: "email" });
          setLoader(false);
          return;
        } else {
          user = await signInWithEmail(email, password);
          if (!user.emailVerified) {
            setError(
              "Please verify your email before logging in. Check your inbox for the verification link."
            );
            setLoader(false);
            return;
          }
        }
      } else {
        if (location.pathname === "/signup") {
          user = await FirebaseAuthentication.createUserWithEmailAndPassword({
            email,
            password,
          });
          await FirebaseAuthentication.sendEmailVerification(
            {
              url: "https://www.crobo.money/login",
              handleCodeInApp: false,
            }
          );
          setVerificationEmailSent(true);
          setEmailVerificationCooldown(true);
          setEmailVerificationCooldownTime(60);
          analytics.track("Signup Success", { method: "email" });
          setLoader(false);
          return;
        } else {
          user = await FirebaseAuthentication.signInWithEmailAndPassword({
            email,
            password,
          });
          const userInfo = await FirebaseAuthentication.getCurrentUser();
          if (!userInfo.user.emailVerified) {
            setError(
              "Please verify your email before logging in. Check your inbox for the verification link."
            );
            setLoader(false);
            return;
          }
          authToken = await FirebaseAuthentication.getIdToken(true);
        }
      }
      getUserInfo(dispatch);
      analytics.track("Login Success", { method: "email", isNewUser });
      dispatch(
        login({
          email: user.email,
          authToken: isWeb() ? user.accessToken : authToken.token,
        })
      );
      // Don't navigate here - let ProtectedRoute handle the navigation
      setLoader(false);

    } catch (error) {
      setLoader(false);
      if (error.code === `${isWeb() ? "auth/" : ""}user-not-found`) {
        setError("User not found. Please sign up.");
        setShowSignupAlert(true);
        setTimeout(() => {
          navigate("/signup");
        }, 2000);
      } else if (error.code === "auth/email-already-in-use" || error.code === "email-already-in-use") {

        if(isWeb()){
        const user = await signInWithEmail(email, password);
        if (!user.emailVerified) {
          setVerificationEmailSignup(true);
          setError(`You have already registered with this email. Please check your inbox for the verification link and complete the verification process.`)
        } else {
          setError(
            "Email is already in use and verified. Please login instead."
          );
        }}else {
          await FirebaseAuthentication.signInWithEmailAndPassword({
            email,
            password,
          });
          const userInfo = await FirebaseAuthentication.getCurrentUser();
          if (!userInfo.user.emailVerified) {
            setVerificationEmailSignup(true);
            setError(`You have already registered with this email. Please check your inbox for the verification link and complete the verification process.`)
          } else {
            setError(
              "Email is already in use and verified. Please login instead."
            );
          }
        }
      }else {
        setError(firebaseErrorHandler(error.code));
      }
      analytics.track(
        location.pathname === "/signup"
          ? "Email Signup Failed"
          : "Email Login Failed",
        { error: error.message }
      );
    }
  };

  const handleForgotPassword = async () => {
    try {
      setLoader(true);
      await resetPassword(email);
      setResetEmailSent(true);
      analytics.track("Password Reset Email Sent", { email });
      setLoader(false);
    } catch (error) {
      setError(error.message);
      setLoader(false);
      analytics.track("Password Reset Failed", { error: error.message });
    }
  };

  const renderPhoneNumberInput = () => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <OutlinedInput
          placeholder="(123) 456-7890"
          value={phone}
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            "& .MuiOutlinedInput-input": {
              py: 1.5,
              fontSize: "1.1rem",
            },
          }}
          type="tel"
          disabled={otpSent}
          onChange={(e) => {
            const p = e.target.value;
            setPhone(p.toString());
            if (p.toString().length > 10) {
              setError("Invalid Phone Number");
            } else {
              setError(null);
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRight: 1,
                  borderColor: "grey.300",
                  pr: 1,
                  mr: 1,
                }}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg"
                  height={20}
                  width={30}
                  alt="+1"
                  style={{ borderRadius: 2 }}
                />
                <Typography sx={{ ml: 1, color: "text.secondary" }}>
                  +1
                </Typography>
              </Box>
            </InputAdornment>
          }
          endAdornment={
            otpSent && (
              <IconButton
                onClick={handleChangePhoneNumber}
                color="brand"
                variant="outlined"
              >
                <EditIcon />
              </IconButton>
            )
          }
          fullWidth
          inputProps={{
            maxLength: 10,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
        />
      </Box>
    </Box>
  );

  const renderOtpInput = () => (
    <Box sx={{ py: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
        Enter verification code
      </Typography>
      <OTPInput
        value={otp}
        onChange={setOtp}
        numInputs={6}
        shouldAutoFocus={true}
        inputType="number"
        renderSeparator={<span style={{ width: "4px" }} />}
        renderInput={(props) => (
          <input
            {...props}
            style={{
              width: "100%",
              height: "40px",
              borderRadius: "8px",
              border: "1px solid #000000",
              fontSize: 18,
              textAlign: "center",
              fontFamily: "Arial",
            }}
          />
        )}
        containerStyle={{
          justifyContent: "center",
          gap: "2px",
        }}
      />

      <Box sx={{ mt: 3 }}>
        {loader || loading ? (
          <Loader />
        ) : (
          <Button
            variant="contained"
            onClick={handleVerifyOtp}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              backgroundColor: "brand.main",
              color: "white",
              "&:hover": {
                backgroundColor: "brand.dark",
              },
              "&.Mui-disabled": {
                backgroundColor: "grey.300",
                color: "grey.500",
              },
            }}
            disabled={otp.length !== 6}
          >
            Verify code
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography color="text.secondary" variant="body2">
          {cooldown
            ? `Resend code in ${cooldownTime}s`
            : "Didn't receive the code?"}
        </Typography>
        <Button
          onClick={() =>
            handleResendOtp(phone, setIsResendDisabled, handleSignInWithPhone)
          }
          disabled={isResendDisabled}
          sx={{
            mt: 1,
            textTransform: "none",
            fontSize: "0.9rem",
          }}
        >
          Send again
        </Button>
      </Box>
    </Box>
  );

  const renderProceedButton = () => (
    <Box sx={{ mt: 2 }}>
      {loader || loading ? (
        <Loader />
      ) : (
        <Button
          onClick={handleSignInWithPhone}
          id="sign-in-button"
          variant="contained"
          fullWidth
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 400,
            backgroundColor: "brand.main",
            color: "white",
            "&:hover": {
              backgroundColor: "brand.dark",
            },
            "&.Mui-disabled": {
              backgroundColor: "grey.300",
              color: "grey.500",
            },
          }}
          disabled={phone.length !== 10 || cooldown}
        >
          Continue
        </Button>
      )}
    </Box>
  );

  const renderForgotPassword = () => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
      >
        Enter your email address and we'll send you instructions to reset your
        password.
      </Typography>
      <OutlinedInput
        placeholder="Email"
        value={email}
        type="email"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          mb: 2,
          "& .MuiOutlinedInput-input": {
            py: 1.5,
            fontSize: "1.1rem",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[300],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[400],
          },
        }}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
      />
      <Box sx={{ mt: 2 }}>
        {loader || loading ? (
          <Loader />
        ) : (
          <>
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              color="brand"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                mb: 2,
              }}
              disabled={!email}
            >
              Send Reset Link
            </Button>
            <Button
              onClick={() => setShowForgotPassword(false)}
              variant="outlined"
              color="brand"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              Back to Login
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  const renderEmailLogin = () => (
    <Box sx={{ mb: 2 }}>
      <OutlinedInput
        placeholder="Email"
        value={email}
        type="email"
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          mb: 2,
          "& .MuiOutlinedInput-input": {
            py: 1.5,
            fontSize: "1.1rem",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[300],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[400],
          },
        }}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
      />
      <OutlinedInput
        placeholder="Password"
        value={password}
        type={showPassword ? "text" : "password"}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          mb: location.pathname === "/signup" ? 2 : 0,
          "& .MuiOutlinedInput-input": {
            py: 1.5,
            fontSize: "1.1rem",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[300],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.divider
                : theme.palette.grey[400],
          },
        }}
        onChange={(e) => {
          setPassword(e.target.value);
          if (location.pathname === "/signup") {
            const error = validatePassword(e.target.value);
            setPasswordError(error);
          }
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              sx={{ color: "text.secondary" }}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </InputAdornment>
        }
        fullWidth
      />
      {location.pathname === "/signup" && passwordError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mb: 1, display: "block" }}
        >
          {passwordError}
        </Typography>
      )}
      {location.pathname === "/signup" && (
        <OutlinedInput
          placeholder="Confirm Password"
          value={confirmPassword}
          type={showConfirmPassword ? "text" : "password"}
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            "& .MuiOutlinedInput-input": {
              py: 1.5,
              fontSize: "1.1rem",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.divider
                  : theme.palette.grey[300],
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.divider
                  : theme.palette.grey[400],
            },
          }}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (e.target.value !== password) {
              setError("Passwords do not match");
            } else {
              setError("");
            }
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
                sx={{ color: "text.secondary" }}
              >
                {showConfirmPassword ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </IconButton>
            </InputAdornment>
          }
          fullWidth
        />
      )}
      {location.pathname === "/signup" &&
        confirmPassword &&
        password !== confirmPassword && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: "block" }}
          >
            Passwords do not match
          </Typography>
        )}
      {!location.pathname.includes("signup") && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Button
            onClick={() => setShowForgotPassword(true)}
            variant="text"
            id="forgot-password-btn"
            sx={{
              textTransform: "none",
              p: 0,
              color: (theme) => theme.palette.text.primary,
            }}
          >
            Forgot password?
          </Button>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        {loader || loading ? (
          <Loader />
        ) : (
          <Button
            onClick={handleEmailLogin}
            variant="contained"
            fullWidth
            id="email-login-btn"
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              backgroundColor: "brand.main",
              color: "white",
              "&:hover": {
                backgroundColor: "brand.dark",
              },
              "&.Mui-disabled": {
                backgroundColor: "grey.300",
                color: "grey.500",
              },
            }}
            disabled={
              !email ||
              !password ||
              (location.pathname === "/signup" && !confirmPassword)
            }
          >
            {location.pathname === "/signup" ? "Sign up" : "Login"} with Email
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderLoginMethods = () => (
    <Box sx={{ mb: 3 }}>
      {location.pathname === "/login" ? (
        <>
          <Button
            onClick={() => {
              setError("");
              setLoginPopupClosed(false);
              setLoginMethod("phone");
              setShowLoginForm(true);
            }}
            variant="contained"
            fullWidth
            startIcon={<PhoneIcon sx={{ color: "white" }} />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 400,
              mb: 2,
              height: "48px",
              backgroundColor: "brand.main",
              color: "white",
              "&:hover": {
                backgroundColor: "brand.dark",
              },
            }}
          >
            {location.pathname === "/signup" ? "Sign up" : "Login"} with Phone Number
          </Button>
          <Button
            onClick={() => {
              setError("");
              setLoginPopupClosed(false);
              setLoginMethod("email");
              setShowLoginForm(true);
            }}
            variant="contained"
            fullWidth
            startIcon={<EmailIcon sx={{ color: "white" }} />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 400,
              height: "48px",
              backgroundColor: "brand.main",
              color: "white",
              "&:hover": {
                backgroundColor: "brand.dark",
              },
            }}
          >
            {location.pathname === "/signup" ? "Sign up" : "Login"} with Email
          </Button>
        </>
      ) : (
        <Button
          onClick={() => {
            setLoginMethod("email");
            setShowLoginForm(true);
          }}
          variant="contained"
          fullWidth
          id="email-login-method-btn"
          startIcon={<EmailIcon sx={{ color: "white" }} />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 400,
            height: "48px",
            backgroundColor: "brand.main",
            color: "white",
            "&:hover": {
              backgroundColor: "brand.dark",
            },
          }}
        >
          {location.pathname === "/signup" ? "Sign up" : "Login"} with Email
        </Button>
      )}
    </Box>
  );

  const handleBack = () => {
    setShowLoginForm(false);
    setLoginMethod("");
    setError("");
    setEmail("");
    setLoginMethod("");
    setPassword("");
    setPhone("");
    setOtpSent(false);
  };

  const renderBackButton = () => (
    <Box sx={{ mb: 3 }}>
      <Button
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        id="back-btn"
        sx={{ textTransform: "none", color: "text.secondary" }}
      >
        Back to login options
      </Button>
    </Box>
  );

  const renderTermsAndConditions = () => (
    <Grid>
      {location.pathname === "/signup" && (
        <Typography
          variant="body2"
          color="text.secondary"
          id="terms-text"
          sx={{ paddingRight: 1, mt: 2 }}
        >
          By continuing, you agree to our{" "}
          <Link to={"/tnc"} viewTransition>
            Terms of service
          </Link>{" "}
          and confirm that you have read our{" "}
          <Link to={"/privacy-policy"} viewTransition>
            Privacy policy
          </Link>{" "}
          to learn how we collect, use, and share your data.
        </Typography>
      )}
    </Grid>
  );

  const renderFooterLinks = () => (
    <Box>
      {location.pathname === "/signup" ? (
        <Typography textAlign={"center"}>
          Already have an account?
          <Link to={"/login"} viewTransition>
            <Button
              variant="text"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              LOGIN
            </Button>
          </Link>
        </Typography>
      ) : (
        <Typography textAlign={"center"}>
          Don't have an account?
          <Link to={"/signup"} viewTransition>
            <Button
              variant="text"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              SIGN UP
            </Button>
          </Link>
        </Typography>
      )}
    </Box>
  );

  const renderSocialButtons = () => (
    <Box sx={{ mt: 3, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          my: 3,
        }}
      >
        <Divider sx={{ flex: 1 }} />
        <Typography sx={{ px: 2, color: "text.secondary" }} variant="body2">
          OR
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
      <GoogleButton
        onClick={handleGoogleSignIn}
        sx={{
          borderRadius: 2,
          py: 1.5,
          border: 1,
          borderColor: "grey.300",
          mb: 4,
        }}
      />
      <AppleButton handleSignInWithApple={handleAppleSignIn} />
    </Box>
  );

  return (
    <CroboContainer>
      <Container maxWidth="sm" sx={{ px: 2 }}>
        <Snackbar
          open={showSignupAlert}
          autoHideDuration={3000}
          onClose={() => setShowSignupAlert(false)}
          className="notch"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            sx={{
              width: "100%",
              backgroundColor: "white",
              boxShadow: 3,
              fontWeight: 600,
              border: "1px solid #000",
              borderColor: "brand.main",
            }}
          >
            User not found. Redirecting to signup...
          </Alert>
        </Snackbar>
        <Snackbar
          open={resetEmailSent}
          className="notch"
          autoHideDuration={3000}
          onClose={() => setResetEmailSent(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "white",
              boxShadow: 3,
              fontWeight: 600,
              border: "1px solid #000",
              borderColor: "brand.main",
            }}
          >
            Password reset email sent! Please check your inbox.
          </Alert>
        </Snackbar>
        <Dialog
          open={verificationEmailSent}
          onClose={() => setVerificationEmailSent(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              bgcolor: "background.paper",
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setVerificationEmailSent(false)}
              sx={{
                position: "absolute",
                right: -10,
                top: -10,
                color: "text.secondary",
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
              Verify your email
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
              A verification link has been sent to <strong>{email}</strong>.
              Please check your inbox and verify your email to continue.
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              disabled={emailVerificationCooldown}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                borderColor: "brand.main",
                color: "brand.main",
                "&:hover": {
                  borderColor: "brand.dark",
                  backgroundColor: "transparent",
                },
                "&.Mui-disabled": {
                  borderColor: "grey.300",
                  color: "grey.500",
                },
              }}
              onClick={async () => {
                try {
                  if (emailVerificationCooldown) {
                    setError(
                      "Please wait before requesting another verification email."
                    );
                    return;
                  }

                  if (isWeb()) {
                    await sendEmailVerification(auth.currentUser, {
                      url: "https://www.crobo.money/login",
                      handleCodeInApp: false,
                    });
                  } else {
                    await FirebaseAuthentication.sendEmailVerification();
                  }

                  setEmailVerificationCooldown(true);
                  setEmailVerificationCooldownTime(60);
                  setError("Verification email resent successfully!");
                } catch (error) {
                  setError(
                    "Failed to resend verification email. Please try again."
                  );
                }
              }}
            >
              {emailVerificationCooldown
                ? `Resend email in ${emailVerificationCooldownTime}s`
                : "Resend email"}
            </Button>
          </Box>
        </Dialog>
        <Dialog
          open={verificationEmailSignup}
          onClose={() => setVerificationEmailSignup(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              bgcolor: "background.paper",
            },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setVerificationEmailSignup(false)}
              sx={{
                position: "absolute",
                right: -10,
                top: -10,
                color: "text.secondary",
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
              Verify your email
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
              A verification email has already been sent to{" "}
              <strong>{email}</strong>. Please check your inbox and verify your
              email to continue.
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                borderColor: "brand.main",
                color: "brand.main",
                "&:hover": {
                  borderColor: "brand.dark",
                  backgroundColor: "transparent",
                },
              }}
              onClick={async () => {
                try {
                  if (isWeb()) {
                    await sendEmailVerification(auth.currentUser, {
                      url: "https://www.crobo.money/login",
                      handleCodeInApp: false,
                    });
                  } else {
                    await FirebaseAuthentication.sendEmailVerification();
                  }
                  setVerificationEmailSignup(false);
                  setError("Verification email sent successfully!");
                } catch (error) {
                  setError(
                    "Failed to send verification email. Please try again."
                  );
                }
              }}
            >
              Resend email
            </Button>
          </Box>
        </Dialog>
        <Snackbar
          open={loginPopupClosed}
          className="notch"
          autoHideDuration={3000}
          onClose={() => setLoginPopupClosed(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "white",
              boxShadow: 3,
              fontWeight: 600,
              border: "1px solid #000",
              borderColor: "brand.main",
            }}
          >
            The login popup has been closed by the user. Please try again.
          </Alert>
        </Snackbar>
        <Box
          sx={{
            pt: { xs: 2, sm: 4 },
            pb: 4,
          }}
        >
          {loader || loading ? (
            <Loader />
          ) : (
            <>
              <Box sx={{ mb: 4, textAlign: "center" }}>
                {showForgotPassword ? (
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
                    Forgot Password
                  </Typography>
                ) : location.pathname === "/signup" ? (
                  <>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
                      Create your account
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sign up to send money to your family in India
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 400 }}>
                      Welcome back
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Login to send money to your family in India
                    </Typography>
                  </>
                )}
              </Box>

              {showForgotPassword ? (
                renderForgotPassword()
              ) : showLoginForm ? (
                <>
                  {location.pathname === "/login" && renderBackButton()}
                  {loginMethod === "phone" && location.pathname === "/login" ? (
                    <>
                      {renderPhoneNumberInput()}
                      {error && <ErrorComponent error={error} />}
                      {!otpSent ? renderProceedButton() : renderOtpInput()}
                    </>
                  ) : (
                    <>
                      {renderEmailLogin()}
                      {error && <ErrorComponent error={error} />}
                    </>
                  )}
                </>
              ) : (
                <>
                  {renderLoginMethods()}
                  {!otpSent && !loader && renderSocialButtons()}
                </>
              )}
              {renderTermsAndConditions()}

              <Box>{!showForgotPassword && <>{renderFooterLinks()}</>}</Box>
            </>
          )}
        </Box>
      </Container>
    </CroboContainer>
  );
};

export default Login;
